// frontend/src/pages/PreOrder.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBoxOpen,
  faCalendarCheck,
  faCheck,
  faCircleInfo,
  faClipboardCheck,
  faCreditCard,
  faLeaf,
  faLocationDot,
  faPhone,
  faReceipt,
  faShieldHalved,
  faTruck,
  faWarehouse,
  faWeightHanging,
  faSpinner,
  faExclamationTriangle,
  faGlobe,
  faShip,
  faBuilding,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { createPreOrder, getAllShipments } from "../api";
import "./PreOrder.scss";
import logoImage from "../assets/Logo.png";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// SUPPLIER CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// PACKAGES
// ============================================
const PACKAGES = [
  {
    id: "25kg",
    label: "25 kg",
    quantityKg: 25,
    regularPrice: 105,
    preOrderPrice: 98,
    deposit: 49,
    balance: 49,
    bestFor: "Regular farms and growing herds",
    feedCoverage: "Mixes up to 6,250 kg feed",
    savings: 7,
  },
  {
    id: "50kg",
    label: "50 kg",
    quantityKg: 50,
    regularPrice: 195,
    preOrderPrice: 180,
    deposit: 90,
    balance: 90,
    bestFor: "Large farmers, bulk buyers, and resellers",
    feedCoverage: "Mixes up to 12,500 kg feed",
    savings: 15,
  },
];

// ============================================
// CONSTANTS
// ============================================
const ANIMAL_TYPES = ["Cattle", "Goats", "Sheep", "Pigs", "Poultry", "Other"];
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash deposit" },
  { value: "orange_money", label: "Orange Money" },
  { value: "afrimoney", label: "Afrimoney" },
  { value: "wave", label: "WAVE" },
  { value: "bank_transfer", label: "Bank transfer" },
];
const DEFAULT_EXCHANGE_RATE = 23500;
const WHATSAPP_NUMBER = "23231030808";

// ============================================
// HELPERS
// ============================================

/**
 * Format price in SLL (Sierra Leone Leone) - DEFAULT
 */
const formatSLL = (value) =>
  new Intl.NumberFormat("en-SL", {
    style: "currency",
    currency: "SLL",
    maximumFractionDigits: 0,
  }).format(value || 0);

/**
 * Format price in USD - SECONDARY
 */
const formatUSD = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);

/**
 * Format price in MYR (Malaysian Ringgit) - TERTIARY
 */
const formatMYR = (value) =>
  new Intl.NumberFormat("ms-MY", {
    style: "currency",
    currency: "MYR",
    maximumFractionDigits: 2,
  }).format(value || 0);

/**
 * Convert USD to SLL
 */
const usdToSLL = (usd, rate = DEFAULT_EXCHANGE_RATE) => usd * rate;

/**
 * Convert USD to MYR
 */
const usdToMYR = (usd, rate = 4.75) => usd * rate;

const createWhatsAppText = ({ form, selectedPackage, qty, totals }) => {
  return encodeURIComponent(
    `Hello Kenali Enterprise, I want to pre-order GrowPro.\n\n` +
      `Name: ${form.fullName || ""}\n` +
      `Farm/Business: ${form.farmName || ""}\n` +
      `Phone: ${form.phone || ""}\n` +
      `Location: ${form.district || ""}\n` +
      `Animal Type: ${form.animalType || ""}\n` +
      `Package: ${selectedPackage.label}\n` +
      `Quantity: ${qty}\n` +
      `Total: ${formatUSD(totals.totalUSD)} (${formatSLL(totals.totalSLL)})\n` +
      `Deposit Now: ${formatUSD(totals.depositUSD)} (${formatSLL(totals.depositSLL)})\n` +
      `Balance on Pickup: ${formatUSD(totals.balanceUSD)} (${formatSLL(totals.balanceSLL)})\n` +
      `Payment Method: ${form.paymentMethod}\n\n` +
      `Please confirm my pre-order and pickup details.`,
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const PreOrder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency, formatPrice, rates } = useCurrency();
  const [selectedPackId, setSelectedPackId] = useState("25kg");
  const [quantity, setQuantity] = useState(1);
  const [exchangeRate, setExchangeRate] = useState(DEFAULT_EXCHANGE_RATE);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [inventoryShipments, setInventoryShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(true);

  const [form, setForm] = useState({
    fullName: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    farmName: user?.farmDetails?.farmName || "",
    district: "",
    city: "Freetown",
    street: "30 Kissy Road",
    animalType: "Cattle",
    customerType: "livestock_farmer",
    orderType: "pickup",
    paymentMethod: "cash",
    notes: "",
    termsAccepted: false,
  });

  // ============================================
  // FETCH SHIPMENTS
  // ============================================
  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await getAllShipments({ limit: 50 });
        const shipments = response.data?.shipments || [];
        const inventory = shipments.filter(
          (s) => s.orderType === "inventory" && s.isActive !== false,
        );
        setInventoryShipments(inventory);
      } catch (error) {
        console.error("Error fetching inventory shipments:", error);
      } finally {
        setLoadingShipments(false);
      }
    };
    fetchShipments();
  }, []);

  // ============================================
  // COMPUTED
  // ============================================
  const selectedPackage =
    PACKAGES.find((pkg) => pkg.id === selectedPackId) || PACKAGES[0];

  const totals = useMemo(() => {
    const totalUSD = selectedPackage.preOrderPrice * quantity;
    const depositUSD = selectedPackage.deposit * quantity;
    const balanceUSD = selectedPackage.balance * quantity;
    const rate = exchangeRate || DEFAULT_EXCHANGE_RATE;
    return {
      totalUSD,
      depositUSD,
      balanceUSD,
      totalSLL: totalUSD * rate,
      depositSLL: depositUSD * rate,
      balanceSLL: balanceUSD * rate,
      totalMYR: totalUSD * 4.75,
      depositMYR: depositUSD * 4.75,
      balanceMYR: balanceUSD * 4.75,
      totalKg: selectedPackage.quantityKg * quantity,
      savings: selectedPackage.savings * quantity,
    };
  }, [selectedPackage, quantity, exchangeRate]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!form.fullName || !form.phone || !form.district) {
      setErrorMessage(
        "Please add your name, phone number, and district/location.",
      );
      return;
    }

    if (!form.termsAccepted) {
      setErrorMessage("Please accept the pre-order terms and conditions.");
      return;
    }

    setSubmitting(true);

    try {
      const preOrderData = {
        customerInfo: {
          fullName: form.fullName,
          farmOrBusinessName: form.farmName || "",
          phone: form.phone,
          whatsapp: form.phone,
          email: form.email || "",
          location: form.district,
          district: form.district,
        },
        farmInfo: {
          customerType: form.customerType || "livestock_farmer",
          animalTypes: [form.animalType?.toLowerCase() || "cattle"],
          notes: form.notes || "",
        },
        items: [
          {
            packSizeKg: selectedPackage.quantityKg,
            quantity: quantity,
          },
        ],
        paymentMethod: form.paymentMethod,
        pickup: {
          type: form.orderType,
          address: form.street || "30 Kissy Road, Freetown, Sierra Leone",
          instructions: form.notes || "",
        },
        source: "website",
        termsAccepted: form.termsAccepted,
        marketingConsent: false,
        customerNotes: form.notes || "",
        exchangeRate: {
          usdToSll: exchangeRate,
        },
        supplier: {
          name: SUPPLIER.name,
          country: SUPPLIER.country,
        },
      };

      const response = await createPreOrder(preOrderData);

      setSuccessMessage(
        response?.data?.message ||
          `✅ Pre-order submitted successfully! Kenali Enterprise will confirm your deposit and reserve your stock when the shipment from ${SUPPLIER.name} (${SUPPLIER.country}) arrives.`,
      );

      setTimeout(() => {
        if (user) {
          navigate("/dashboard");
        } else {
          navigate("/");
        }
      }, 3000);
    } catch (error) {
      console.error("Pre-order error:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to submit pre-order. Please try again or contact Kenali Enterprise by WhatsApp.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${createWhatsAppText(
    {
      form,
      selectedPackage,
      qty: quantity,
      totals,
    },
  )}`;

  // Find active incoming shipment
  const activeShipment = inventoryShipments.find(
    (s) =>
      s.status === "in_transit" ||
      s.status === "shipped" ||
      s.status === "customs",
  );

  // ============================================
  // RENDER
  // ============================================
  return (
    <motion.main
      className="kenali-preorder-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="kp-hero">
        <div className="kp-hero__bg" />
        <div className="kp-container kp-hero__grid">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="kp-brand-mark">
              <img
                src={logoImage}
                alt="Kenali Enterprise"
                onError={(e) =>
                  (e.currentTarget.src = "/images/KenaliLogo.png")
                }
              />
              <div>
                <span>Kenali Enterprise</span>
                <small>Nutrition • Growth • Prosperity</small>
              </div>
            </div>

            <div className="kp-eyebrow">
              <FontAwesomeIcon icon={faCalendarCheck} /> First-shipment priority
              list now open
            </div>
            <h1>Pre-order Kenali GrowPro for your farm</h1>
            <p>
              Premium livestock feed supplement for cattle, goats, sheep, pigs,
              and poultry. Sourced from <strong>{SUPPLIER.name}</strong> in{" "}
              <strong>{SUPPLIER.country}</strong>. Reserve your 25 kg or 50 kg
              pack today with 50% deposit now and 50% balance upon pickup.
            </p>
            <div className="kp-hero__actions">
              <a href="#preorder-form" className="kp-btn kp-btn--gold">
                Reserve GrowPro <FontAwesomeIcon icon={faArrowRight} />
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="kp-btn kp-btn--whatsapp"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Order
              </a>
            </div>
          </motion.div>

          <motion.aside
            className="kp-hero-card"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <div className="kp-hero-card__badge">
              <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Origin
            </div>
            <h2>GrowPro + Feed = Complete Mixture</h2>
            <p>Recommended inclusion rate:</p>
            <strong>4 kg GrowPro per 1 tonne / 1,000 kg of feed</strong>
            <ul>
              <li>
                <span>1 kg</span> GrowPro + 250 kg feed
              </li>
              <li>
                <span>2 kg</span> GrowPro + 500 kg feed
              </li>
              <li>
                <span>4 kg</span> GrowPro + 1,000 kg feed
              </li>
            </ul>
            <div className="kp-hero-card__shipping">
              <FontAwesomeIcon icon={faShip} /> Shipped from{" "}
              {SUPPLIER.portLoading}
            </div>
          </motion.aside>
        </div>
      </section>

      {/* ============================================
          INCOMING SHIPMENT NOTICE
          ============================================ */}
      {activeShipment && (
        <div
          className="kp-container"
          style={{ marginTop: "-1rem", marginBottom: "2rem" }}
        >
          <div className="kp-shipment-notice">
            <div className="kp-shipment-notice__content">
              <FontAwesomeIcon icon={faShip} />
              <div>
                <strong>New shipment from {SUPPLIER.name} incoming!</strong>
                {activeShipment.estimatedDelivery && (
                  <span>
                    Est. arrival:{" "}
                    {new Date(
                      activeShipment.estimatedDelivery,
                    ).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <span className="kp-shipment-notice__badge">
              <FontAwesomeIcon icon={faBell} /> Pre-order customers get priority
            </span>
          </div>
        </div>
      )}

      {/* ============================================
          TRUST ROW
          ============================================ */}
      <section className="kp-container kp-trust-row" aria-label="Benefits">
        <div>
          <FontAwesomeIcon icon={faLeaf} />
          <span>Better growth support</span>
        </div>
        <div>
          <FontAwesomeIcon icon={faShieldHalved} />
          <span>Immunity support</span>
        </div>
        <div>
          <FontAwesomeIcon icon={faWeightHanging} />
          <span>Strong bones & muscles</span>
        </div>
        <div>
          <FontAwesomeIcon icon={faTruck} />
          <span>Pickup or delivery options</span>
        </div>
        <div>
          <FontAwesomeIcon icon={faGlobe} />
          <span>{SUPPLIER.country} sourced</span>
        </div>
      </section>

      {/* ============================================
          MAIN CONTENT
          ============================================ */}
      <section className="kp-container kp-content-grid">
        {/* ========== LEFT COLUMN ========== */}
        <div className="kp-left-column">
          {/* Package Selection */}
          <section className="kp-card">
            <h2>
              <FontAwesomeIcon icon={faBoxOpen} /> Choose your pre-order package
            </h2>
            <p className="kp-muted">
              Pre-orders are accepted for 25 kg and 50 kg packs only. Sourced
              from {SUPPLIER.name}, {SUPPLIER.country}.
            </p>
            <div className="kp-package-grid">
              {PACKAGES.map((pkg) => (
                <motion.button
                  key={pkg.id}
                  type="button"
                  className={`kp-package ${selectedPackId === pkg.id ? "is-selected" : ""}`}
                  onClick={() => setSelectedPackId(pkg.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="kp-package__label">{pkg.label}</span>
                  <span className="kp-package__price">
                    {formatUSD(pkg.preOrderPrice)}
                  </span>
                  <small>Regular {formatUSD(pkg.regularPrice)}</small>
                  <span className="kp-package__savings">
                    Save {formatUSD(pkg.savings)}
                  </span>
                  <em>{pkg.bestFor}</em>
                  <strong>{pkg.feedCoverage}</strong>
                </motion.button>
              ))}
            </div>
          </section>

          {/* Price Summary */}
          <section className="kp-card kp-pricing-card">
            <h2>
              <FontAwesomeIcon icon={faReceipt} /> Pre-order price summary
            </h2>
            <div className="kp-price-table">
              <div className="kp-price-table__head">
                <span>Pack</span>
                <span>Regular</span>
                <span>Pre-order</span>
                <span>Deposit</span>
                <span>Balance</span>
                <span>Save</span>
              </div>
              {PACKAGES.map((pkg) => (
                <div className="kp-price-table__row" key={pkg.id}>
                  <span>{pkg.label}</span>
                  <span>{formatUSD(pkg.regularPrice)}</span>
                  <span className="kp-price-table__preorder">
                    {formatUSD(pkg.preOrderPrice)}
                  </span>
                  <span>{formatUSD(pkg.deposit)}</span>
                  <span>{formatUSD(pkg.balance)}</span>
                  <span className="kp-price-table__savings">
                    Save {formatUSD(pkg.savings)}
                  </span>
                </div>
              ))}
            </div>
            <div className="kp-note">
              <FontAwesomeIcon icon={faCircleInfo} />
              Prices are shown in USD. Leone (SLL) equivalent is shown below
              using the current exchange rate.
            </div>
          </section>
        </div>

        {/* ========== PRE-ORDER FORM ========== */}
        <form
          id="preorder-form"
          className="kp-card kp-form"
          onSubmit={handleSubmit}
        >
          <div className="kp-form__header">
            <div>
              <h2>
                <FontAwesomeIcon icon={faClipboardCheck} /> GrowPro Pre-Order
                Form
              </h2>
              <p>
                Reserve stock before public release. 50% deposit now, 50%
                balance upon pickup. Sourced from {SUPPLIER.name} (
                {SUPPLIER.country}).
              </p>
            </div>
            <span className="kp-form__badge">25 kg & 50 kg only</span>
          </div>

          <div className="kp-form-grid">
            <label>
              Full Name *
              <input
                name="fullName"
                value={form.fullName}
                onChange={handleFormChange}
                placeholder="Customer name"
                required
              />
            </label>
            <label>
              Phone / WhatsApp *
              <input
                name="phone"
                value={form.phone}
                onChange={handleFormChange}
                placeholder="+232 ..."
                required
              />
            </label>
            <label>
              Farm / Business Name
              <input
                name="farmName"
                value={form.farmName}
                onChange={handleFormChange}
                placeholder="Farm or business"
              />
            </label>
            <label>
              Email
              <input
                name="email"
                value={form.email}
                onChange={handleFormChange}
                placeholder="Optional"
              />
            </label>
            <label>
              District / Location *
              <input
                name="district"
                value={form.district}
                onChange={handleFormChange}
                placeholder="Enter your district"
                required
              />
            </label>
            <label>
              City / Town
              <input
                name="city"
                value={form.city}
                onChange={handleFormChange}
                placeholder="Freetown"
              />
            </label>
            <label>
              Pickup / Delivery Address
              <input
                name="street"
                value={form.street}
                onChange={handleFormChange}
                placeholder="30 Kissy Road, Freetown"
              />
            </label>
            <label>
              Animal Type
              <select
                name="animalType"
                value={form.animalType}
                onChange={handleFormChange}
              >
                {ANIMAL_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Package Size
              <select
                value={selectedPackId}
                onChange={(e) => setSelectedPackId(e.target.value)}
              >
                {PACKAGES.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.label} - {formatUSD(pkg.preOrderPrice)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Quantity
              <input
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, Number(e.target.value || 1)))
                }
              />
            </label>
            <label>
              Payment Method
              <select
                name="paymentMethod"
                value={form.paymentMethod}
                onChange={handleFormChange}
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Pickup or Delivery
              <select
                name="orderType"
                value={form.orderType}
                onChange={handleFormChange}
              >
                <option value="pickup">Pickup</option>
                <option value="delivery">Delivery</option>
              </select>
            </label>
          </div>

          <label className="kp-full-label">
            Customer Notes
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleFormChange}
              placeholder="Add delivery notes, preferred pickup date, farm needs, or product questions."
            />
          </label>

          {/* ========== TOTALS - SLL DEFAULT ========== */}
          <div className="kp-totals">
            <div>
              <span>Total kg</span>
              <strong>{totals.totalKg.toLocaleString()} kg</strong>
            </div>
            <div className="kp-totals__primary">
              <span>Total pre-order (SLL)</span>
              <strong>{formatSLL(totals.totalSLL)}</strong>
              <small>{formatUSD(totals.totalUSD)}</small>
            </div>
            <div className="kp-totals__deposit">
              <span>Deposit now (SLL)</span>
              <strong>{formatSLL(totals.depositSLL)}</strong>
              <small>{formatUSD(totals.depositUSD)}</small>
            </div>
            <div>
              <span>Balance on pickup (SLL)</span>
              <strong>{formatSLL(totals.balanceSLL)}</strong>
              <small>{formatUSD(totals.balanceUSD)}</small>
            </div>
            <div className="kp-totals__savings">
              <span>You save</span>
              <strong>{formatUSD(totals.savings)}</strong>
            </div>
            <div className="kp-totals__myr">
              <span>MYR (Malaysian Ringgit)</span>
              <strong>{formatMYR(totals.totalMYR)}</strong>
              <small>Reference only</small>
            </div>
          </div>

          <div className="kp-exchange-rate">
            <label>
              Exchange rate: 1 USD =
              <input
                type="number"
                value={exchangeRate}
                onChange={(e) =>
                  setExchangeRate(
                    Number(e.target.value || DEFAULT_EXCHANGE_RATE),
                  )
                }
              />{" "}
              SLL (Leone)
            </label>
          </div>

          <div className="kp-terms-checkbox">
            <label>
              <input
                type="checkbox"
                name="termsAccepted"
                checked={form.termsAccepted}
                onChange={handleFormChange}
              />
              I agree to the pre-order terms: 50% deposit now, remaining 50% due
              before pickup or delivery. Pre-orders are fulfilled from shipments
              arriving from {SUPPLIER.name} ({SUPPLIER.country}).
            </label>
          </div>

          {/* ========== ALERTS ========== */}
          {errorMessage && (
            <div className="kp-alert kp-alert--error">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="kp-alert kp-alert--success">
              <FontAwesomeIcon icon={faCheck} /> {successMessage}
            </div>
          )}

          {/* ========== FORM ACTIONS ========== */}
          <div className="kp-form-actions">
            <motion.button
              type="submit"
              className="kp-btn kp-btn--gold"
              disabled={submitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
                </>
              ) : (
                "Submit pre-order"
              )}
            </motion.button>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="kp-btn kp-btn--whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Send by WhatsApp
            </a>
          </div>

          {!user && (
            <p className="kp-login-note">
              <FontAwesomeIcon icon={faCircleInfo} />
              Website checkout requires an account.{" "}
              <Link to="/login">Log in</Link> or
              <Link to="/register"> create an account</Link>. Cash and WhatsApp
              pre-orders can still be captured manually.
            </p>
          )}
        </form>
      </section>

      {/* ============================================
          TERMS SECTION
          ============================================ */}
      <section className="kp-container kp-terms">
        <div>
          <FontAwesomeIcon icon={faCreditCard} />
          <strong>Pre-order terms</strong>
          <p>
            50% deposit now. Remaining 50% is due before pickup or delivery.
            Receipt is required for collection.
          </p>
        </div>
        <div>
          <FontAwesomeIcon icon={faShip} />
          <strong>Shipment from {SUPPLIER.name}</strong>
          <p>
            Pre-order customers receive priority access when the shipment from{" "}
            {SUPPLIER.country} is cleared and ready for pickup.
          </p>
        </div>
        <div>
          <FontAwesomeIcon icon={faWarehouse} />
          <strong>Inventory notice</strong>
          <p>
            Stock is reserved exclusively for pre-order customers upon shipment
            arrival from {SUPPLIER.portLoading}.
          </p>
        </div>
        <div>
          <FontAwesomeIcon icon={faLocationDot} />
          <strong>Kenali Enterprise</strong>
          <p>30 Kissy Road, Freetown, Sierra Leone</p>
        </div>
        <div>
          <FontAwesomeIcon icon={faPhone} />
          <strong>Contact</strong>
          <p>+232 31 030808 • +1 949 919 4017</p>
        </div>
      </section>

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        /* ========== CONTAINER ========== */
        .kp-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        @media (max-width: 768px) {
          .kp-container {
            padding: 0 1rem;
          }
        }

        /* ========== HERO ========== */
        .kp-hero {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          padding: 4rem 1.5rem 3rem;
          position: relative;
          overflow: hidden;
        }

        .kp-hero__bg {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 500px;
          height: 500px;
          background: rgba(217, 163, 33, 0.05);
          border-radius: 50%;
        }

        .kp-hero__grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        @media (max-width: 992px) {
          .kp-hero__grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
        }

        .kp-hero h1 {
          color: #ffffff;
          font-size: clamp(2rem, 4vw, 2.8rem);
          margin: 0.5rem 0;
        }

        .kp-hero p {
          color: rgba(255, 255, 255, 0.9);
          font-size: clamp(0.938rem, 1.2vw, 1.063rem);
          line-height: 1.7;
          max-width: 600px;
        }

        .kp-hero p strong {
          color: #D9A321;
        }

        .kp-brand-mark {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .kp-brand-mark img {
          height: 50px;
          width: auto;
        }

        .kp-brand-mark div {
          display: flex;
          flex-direction: column;
        }

        .kp-brand-mark span {
          font-size: 1.125rem;
          font-weight: 700;
          color: #ffffff;
          font-family: "Montserrat", sans-serif;
        }

        .kp-brand-mark small {
          font-size: 0.65rem;
          color: #D9A321;
          font-weight: 500;
          letter-spacing: 0.5px;
        }

        .kp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(217, 163, 33, 0.2);
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          color: #D9A321;
          font-weight: 500;
        }

        .kp-hero__actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin-top: 1.5rem;
        }

        .kp-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.875rem;
          text-decoration: none;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          font-family: "Inter", sans-serif;
        }

        .kp-btn--gold {
          background: #D9A321;
          color: #ffffff;
        }

        .kp-btn--gold:hover {
          background: #B8860B;
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(217, 163, 33, 0.35);
        }

        .kp-btn--whatsapp {
          background: #25d366;
          color: #ffffff;
        }

        .kp-btn--whatsapp:hover {
          background: #1da851;
          transform: translateY(-2px);
        }

        .kp-hero-card {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 1.5rem 2rem;
          color: #ffffff;
        }

        .kp-hero-card__badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          background: rgba(217, 163, 33, 0.15);
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          font-size: 0.65rem;
          color: #D9A321;
          font-weight: 600;
        }

        .kp-hero-card h2 {
          font-size: 1.25rem;
          margin: 0.5rem 0;
          color: #ffffff;
        }

        .kp-hero-card p {
          font-size: 0.875rem;
          opacity: 0.8;
          margin-bottom: 0.5rem;
        }

        .kp-hero-card strong {
          display: block;
          font-size: 1.1rem;
          color: #D9A321;
          margin: 0.5rem 0;
        }

        .kp-hero-card ul {
          list-style: none;
          padding: 0;
          margin: 0.5rem 0;
        }

        .kp-hero-card ul li {
          display: flex;
          justify-content: space-between;
          padding: 0.2rem 0;
          font-size: 0.813rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .kp-hero-card ul li:last-child {
          border-bottom: none;
        }

        .kp-hero-card ul li span {
          color: #D9A321;
          font-weight: 600;
        }

        .kp-hero-card__shipping {
          margin-top: 1rem;
          font-size: 0.7rem;
          opacity: 0.7;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        @media (max-width: 768px) {
          .kp-hero {
            padding: 2.5rem 1rem 2rem;
          }
          .kp-hero-card {
            padding: 1.25rem;
          }
        }

        @media (max-width: 480px) {
          .kp-hero__actions {
            flex-direction: column;
          }
          .kp-hero__actions .kp-btn {
            justify-content: center;
          }
        }

        /* ========== SHIPMENT NOTICE ========== */
        .kp-shipment-notice {
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          border: 2px solid #d9a321;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .kp-shipment-notice__content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .kp-shipment-notice__content svg {
          color: #d97706;
          font-size: 1.25rem;
        }

        .kp-shipment-notice__content div {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          color: #92400e;
          font-size: clamp(0.75rem, 1vw, 0.875rem);
        }

        .kp-shipment-notice__content div strong {
          color: #78350f;
        }

        .kp-shipment-notice__badge {
          font-size: 0.75rem;
          color: #78350f;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        @media (max-width: 480px) {
          .kp-shipment-notice {
            flex-direction: column;
            text-align: center;
          }
        }

        /* ========== TRUST ROW ========== */
        .kp-trust-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 1rem;
          padding: 2rem 0;
          text-align: center;
        }

        .kp-trust-row div {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .kp-trust-row svg {
          font-size: 1.5rem;
          color: #D9A321;
        }

        .kp-trust-row span {
          font-size: 0.75rem;
          color: #5A5A5A;
          font-weight: 500;
        }

        @media (max-width: 480px) {
          .kp-trust-row {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.75rem;
            padding: 1.5rem 0;
          }
          .kp-trust-row svg {
            font-size: 1.25rem;
          }
          .kp-trust-row span {
            font-size: 0.65rem;
          }
        }

        /* ========== CONTENT GRID ========== */
        .kp-content-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          padding: 2rem 0 3rem;
        }

        @media (max-width: 992px) {
          .kp-content-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }

        /* ========== CARD ========== */
        .kp-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 1.5rem;
          border: 1px solid #e5e5e5;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .kp-card h2 {
          font-size: 1.125rem;
          color: #006B2E;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .kp-card h2 svg {
          color: #D9A321;
        }

        .kp-muted {
          color: #5A5A5A;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        @media (max-width: 480px) {
          .kp-card {
            padding: 1rem;
          }
          .kp-card h2 {
            font-size: 1rem;
          }
        }

        /* ========== PACKAGE GRID ========== */
        .kp-package-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 480px) {
          .kp-package-grid {
            grid-template-columns: 1fr;
          }
        }

        .kp-package {
          background: #F8F8F8;
          border: 2px solid #e5e5e5;
          border-radius: 12px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          font-family: "Inter", sans-serif;
        }

        .kp-package:hover {
          border-color: #D9A321;
        }

        .kp-package.is-selected {
          border-color: #D9A321;
          background: #fef3c7;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        .kp-package__label {
          display: block;
          font-weight: 700;
          font-size: 1.125rem;
          color: #1F1F1F;
        }

        .kp-package__price {
          display: block;
          font-size: 1.25rem;
          font-weight: 700;
          color: #D9A321;
          margin: 0.25rem 0;
        }

        .kp-package small {
          display: block;
          font-size: 0.7rem;
          color: #5A5A5A;
          text-decoration: line-through;
        }

        .kp-package__savings {
          display: inline-block;
          font-size: 0.6rem;
          background: #16a34a;
          color: #ffffff;
          padding: 0.05rem 0.4rem;
          border-radius: 8px;
          font-weight: 600;
          margin: 0.25rem 0;
        }

        .kp-package em {
          display: block;
          font-size: 0.7rem;
          color: #5A5A5A;
          font-style: normal;
          margin: 0.25rem 0;
        }

        .kp-package strong {
          display: block;
          font-size: 0.65rem;
          color: #006B2E;
          font-weight: 600;
        }

        /* ========== PRICE TABLE ========== */
        .kp-price-table {
          overflow-x: auto;
        }

        .kp-price-table__head,
        .kp-price-table__row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
          gap: 0.5rem;
          padding: 0.4rem 0;
          font-size: 0.75rem;
          border-bottom: 1px solid #e5e5e5;
        }

        .kp-price-table__head {
          font-weight: 600;
          color: #1F1F1F;
          border-bottom: 2px solid #e5e5e5;
        }

        .kp-price-table__row {
          color: #5A5A5A;
        }

        .kp-price-table__row .kp-price-table__preorder {
          color: #D9A321;
          font-weight: 600;
        }

        .kp-price-table__row .kp-price-table__savings {
          color: #16a34a;
          font-weight: 600;
        }

        @media (max-width: 480px) {
          .kp-price-table__head,
          .kp-price-table__row {
            font-size: 0.65rem;
            gap: 0.25rem;
            grid-template-columns: 0.8fr 1fr 1fr 1fr 1fr 0.8fr;
          }
        }

        .kp-note {
          margin-top: 1rem;
          padding: 0.5rem 0.75rem;
          background: #fef3c7;
          border-radius: 8px;
          font-size: 0.75rem;
          color: #92400e;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }

        .kp-note svg {
          color: #D9A321;
          margin-top: 0.1rem;
        }

        /* ========== FORM ========== */
        .kp-form__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .kp-form__header h2 {
          margin-bottom: 0.25rem;
        }

        .kp-form__header p {
          color: #5A5A5A;
          font-size: 0.813rem;
        }

        .kp-form__badge {
          font-size: 0.65rem;
          background: #D9A321;
          color: #ffffff;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          font-weight: 600;
          white-space: nowrap;
        }

        .kp-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 480px) {
          .kp-form-grid {
            grid-template-columns: 1fr;
          }
        }

        .kp-form-grid label {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          font-size: 0.813rem;
          font-weight: 500;
          color: #1F1F1F;
        }

        .kp-form-grid input,
        .kp-form-grid select {
          padding: 0.5rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.875rem;
          font-family: "Inter", sans-serif;
          transition: border-color 0.3s ease;
          background: #ffffff;
          color: #1F1F1F;
        }

        .kp-form-grid input:focus,
        .kp-form-grid select:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        .kp-full-label {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          margin-top: 1rem;
          font-size: 0.813rem;
          font-weight: 500;
          color: #1F1F1F;
        }

        .kp-full-label textarea {
          padding: 0.5rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.875rem;
          font-family: "Inter", sans-serif;
          resize: vertical;
          min-height: 60px;
          transition: border-color 0.3s ease;
          background: #ffffff;
          color: #1F1F1F;
        }

        .kp-full-label textarea:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        /* ========== TOTALS - SLL DEFAULT ========== */
        .kp-totals {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 1rem;
          padding: 1rem;
          background: #F8F8F8;
          border-radius: 12px;
          margin: 1rem 0;
        }

        .kp-totals div {
          display: flex;
          flex-direction: column;
        }

        .kp-totals span {
          font-size: 0.7rem;
          color: #5A5A5A;
        }

        .kp-totals strong {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1F1F1F;
        }

        .kp-totals small {
          font-size: 0.65rem;
          color: #5A5A5A;
        }

        .kp-totals__primary strong {
          color: #006B2E;
          font-size: 1.25rem;
        }

        .kp-totals__deposit strong {
          color: #D9A321;
        }

        .kp-totals__savings {
          background: #dcfce7;
          padding: 0.5rem;
          border-radius: 8px;
        }

        .kp-totals__savings strong {
          color: #16a34a;
        }

        .kp-totals__myr {
          background: #f0f0f0;
          padding: 0.5rem;
          border-radius: 8px;
        }

        .kp-totals__myr strong {
          color: #8b5cf6;
          font-size: 0.938rem;
        }

        @media (max-width: 480px) {
          .kp-totals {
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem;
            padding: 0.75rem;
          }
          .kp-totals strong {
            font-size: 0.938rem;
          }
          .kp-totals__primary strong {
            font-size: 1rem;
          }
        }

        /* ========== EXCHANGE RATE ========== */
        .kp-exchange-rate {
          margin: 0.5rem 0;
        }

        .kp-exchange-rate label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: #5A5A5A;
          flex-wrap: wrap;
        }

        .kp-exchange-rate input {
          width: 80px;
          padding: 0.25rem 0.5rem;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          font-size: 0.75rem;
          font-family: "Inter", sans-serif;
        }

        .kp-exchange-rate input:focus {
          outline: none;
          border-color: #D9A321;
        }

        /* ========== TERMS CHECKBOX ========== */
        .kp-terms-checkbox {
          margin: 0.75rem 0;
        }

        .kp-terms-checkbox label {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: #5A5A5A;
          cursor: pointer;
          line-height: 1.5;
        }

        .kp-terms-checkbox input {
          margin-top: 0.15rem;
          width: 16px;
          height: 16px;
          accent-color: #D9A321;
          flex-shrink: 0;
          cursor: pointer;
        }

        /* ========== ALERTS ========== */
        .kp-alert {
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin: 0.75rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .kp-alert--error {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .kp-alert--success {
          background: #dcfce7;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }

        /* ========== FORM ACTIONS ========== */
        .kp-form-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin-top: 1rem;
        }

        .kp-form-actions .kp-btn {
          flex: 1;
          min-width: 160px;
          justify-content: center;
        }

        .kp-form-actions .kp-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .kp-form-actions {
            flex-direction: column;
          }
          .kp-form-actions .kp-btn {
            min-width: auto;
          }
        }

        .kp-login-note {
          margin-top: 1rem;
          padding: 0.75rem;
          background: #f0f0f0;
          border-radius: 8px;
          font-size: 0.75rem;
          color: #5A5A5A;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .kp-login-note a {
          color: #D9A321;
          font-weight: 600;
        }

        .kp-login-note a:hover {
          text-decoration: underline;
        }

        /* ========== TERMS SECTION ========== */
        .kp-terms {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1.5rem;
          padding: 2rem 0 3rem;
          border-top: 1px solid #e5e5e5;
        }

        .kp-terms div {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .kp-terms svg {
          font-size: 1.25rem;
          color: #D9A321;
        }

        .kp-terms strong {
          font-size: 0.813rem;
          color: #1F1F1F;
        }

        .kp-terms p {
          font-size: 0.75rem;
          color: #5A5A5A;
          margin: 0;
          line-height: 1.5;
        }

        @media (max-width: 480px) {
          .kp-terms {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            padding: 1.5rem 0 2rem;
          }
        }
      `}</style>
    </motion.main>
  );
};

export default PreOrder;
